import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
    FormGroup,
    FormControl,
    ControlLabel,
} from "react-bootstrap";
import "./PasswordReset.css";
import LoaderButton from "../components/LoaderButton";
import { resetPasswordInit, resetPasswordFinish } from "../utils/bedrockAPI";

const MIN_PASSWORD_LENGTH = 8;

export default function PasswordReset(props) {
    const [email, setEmail] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [resetKey, setResetKey] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [newConfirm, setNewConfirm] = useState("");

    const hasResetKey = resetKey && resetKey.length === 36;
    const passwordsMatch = newConfirm.length > 0 && newConfirm === newPassword;
    const passwordLongEnough = newPassword.length >= MIN_PASSWORD_LENGTH;
    const canSetPassword = passwordLongEnough && passwordsMatch;

    useEffect(() => {
        if(props.match.params.resetkey) {
            if(props.match.params.resetkey.length===36) {
                setResetKey(props.match.params.resetkey);
            } else {
                props.setErrorMessage("Password reset link is invalid or expired. Please request a new one.");
                props.setErrorStatus("error-message");
                props.setShowError(true);
            }
        }
    },[props.match.params.resetkey]);

    function getPasswordHint() {
        if(newConfirm.length > 0 && !passwordsMatch) {
            return "Passwords do not match.";
        }
        if(newPassword.length > 0 && !passwordLongEnough) {
            return `Password must be at least ${MIN_PASSWORD_LENGTH} characters.`;
        }
        return `Use at least ${MIN_PASSWORD_LENGTH} characters.`;
    }

    async function handlePasswordReset(e) {
        e.preventDefault();
        setIsLoading(true);
        try {
            await resetPasswordInit(email);
        } catch(e) {
            console.log(e.response);
            props.setErrorMessage(e.response.data.error.message);
            props.setErrorStatus("error-message");
            props.setShowError(true);
            setIsLoading(false);
            return;
        }
        props.setErrorMessage("We sent a reset link to your email. Please check your inbox.");
        props.setErrorStatus("success-message");
        props.setShowError(true);
        setIsLoading(false);
    }

    async function handlePasswordChange(e) {
        e.preventDefault();
        if(!canSetPassword) return;
        setIsLoading(true);
        try {
            await resetPasswordFinish({password:newPassword,resetKey:resetKey});
        } catch(e) {
            console.log(e.response);
            props.setErrorMessage(e.response.data.error.message);
            props.setErrorStatus("error-message");
            props.setShowError(true);
            setResetKey("");
            setIsLoading(false);
            return;
        }
        props.setErrorMessage("Password changed. Please log in.");
        props.setErrorStatus("success-message");
        props.setShowError(true);
        setIsLoading(false);
        props.history.push('/login');
    }

    const hintIsError = (newConfirm.length > 0 && !passwordsMatch)
        || (newPassword.length > 0 && !passwordLongEnough);

    return (
        <div className="password-reset-page">
            <header className="password-reset-header">
                <h1>Reset Your Password</h1>
                <p className="password-reset-subtitle">
                    {hasResetKey
                        ? "Choose a new password for your account."
                        : "Enter your account email and we'll send you a reset link."}
                </p>
            </header>

            <div className="password-reset-panel">
                {hasResetKey ? (
                    <form className="password-reset-form" onSubmit={handlePasswordChange}>
                        <FormGroup controlId="password">
                            <ControlLabel>New Password</ControlLabel>
                            <FormControl
                                autoFocus
                                type="password"
                                autoComplete="new-password"
                                value={newPassword}
                                onChange={e => setNewPassword(e.target.value)}
                            />
                        </FormGroup>
                        <FormGroup controlId="confirmPassword">
                            <ControlLabel>Confirm New Password</ControlLabel>
                            <FormControl
                                type="password"
                                autoComplete="new-password"
                                value={newConfirm}
                                onChange={e => setNewConfirm(e.target.value)}
                            />
                        </FormGroup>
                        <p className={`password-reset-hint ${hintIsError ? "password-reset-hint--error" : ""}`}>
                            {getPasswordHint()}
                        </p>
                        <div className="password-reset-actions">
                            <LoaderButton
                                block
                                type="submit"
                                className="btn-brand-dark"
                                bsSize="large"
                                isLoading={isLoading}
                                disabled={!canSetPassword}
                            >
                                Update Password
                            </LoaderButton>
                            <Link className="password-reset-back" to="/login">Back to login</Link>
                        </div>
                    </form>
                ) : (
                    <form className="password-reset-form" onSubmit={handlePasswordReset}>
                        <FormGroup controlId="email">
                            <ControlLabel>Email</ControlLabel>
                            <FormControl
                                autoFocus
                                type="email"
                                autoComplete="email"
                                value={email}
                                onChange={e => setEmail(e.target.value)}
                            />
                        </FormGroup>
                        <div className="password-reset-actions">
                            <LoaderButton
                                block
                                type="submit"
                                className="btn-brand-dark"
                                bsSize="large"
                                isLoading={isLoading}
                                disabled={!email || email.length === 0}
                            >
                                Send Reset Link
                            </LoaderButton>
                            <Link className="password-reset-back" to="/login">Back to login</Link>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
}
